import { ApolloError, ErrorText, formatErrorMessage } from '@main/core-ui';
import { Action } from '@main/datamap-types';
import {
  DataSubjectRequestForm,
  DsrActionIcon,
  FormOutput,
  SanitizeHtml,
} from '@main/pc-core-components';
import { ID } from '@main/schema-utils';
import { RequestAction } from '@transcend-io/privacy-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { selectDisabledActions } from '../../containers/TakeControl/selectors';
import BackIcon from './BackIcon.svg';
import { takeControlConfirmMessages } from './messages';
import {
  BackIconWrapper,
  BackWrapper,
  ConfirmBannerWrapper,
  ConfirmDescriptionWrapper,
  ConfirmHeaderWrapper,
  IconWrapper,
  TakeControlConfirmWrapper,
} from './wrappers';

/**
 * Props for the TakeControlConfirm component
 */
export interface TakeControlConfirmProps extends Action {
  /** Confirm and perform the selected Take Control option */
  confirmRequest: (values: FormOutput) => void;
  /**
   * ID of the active data subject
   * TODO: https://transcend.height.app/T-17126 - not needed when workflows v2 setup
   */
  activeDataSubjectId: ID<'subject'>;
  /** The active classification of data subject within that organization */
  subjectType: string;
  /** Core identifier of user submitting request */
  coreIdentifier: string;
  /** An error if it occurred during the GraphQL request */
  error?: ApolloError;
  /** Callback when back is clicked */
  goBack?: () => void;
  /** Whether the DSR request successfully submitted */
  submittedSuccessfully?: boolean;
}

/**
 * A second confirmation screen for each action in the Take Control modal
 */
function TakeControlConfirm({
  subjectType,
  confirmRequest,
  submittedSuccessfully,
  error,
  activeDataSubjectId,
  coreIdentifier,
  goBack,
  ...action
}: TakeControlConfirmProps): JSX.Element {
  const { formatMessage } = useIntl();
  const { title, secondaryDescription, icon, type } = action;
  const defaultIcon = <DsrActionIcon type={type} />;

  const disabledActions = useSelector(selectDisabledActions);
  const isDisabled = useMemo(
    () => disabledActions.includes(type),
    [disabledActions, type],
  );

  return (
    <div>
      <BackWrapper onClick={goBack}>
        <BackIconWrapper src={BackIcon} alt="" />
        <div style={{ margin: 0, fontWeight: 600 }}>
          {formatMessage(takeControlConfirmMessages.back)}
        </div>
      </BackWrapper>
      <TakeControlConfirmWrapper>
        <ConfirmBannerWrapper>
          {icon ? <IconWrapper src={icon} alt="" /> : defaultIcon}
          <ConfirmHeaderWrapper>{formatMessage(title)}</ConfirmHeaderWrapper>
        </ConfirmBannerWrapper>
        <ConfirmDescriptionWrapper>
          <SanitizeHtml html={formatMessage(secondaryDescription)} />
        </ConfirmDescriptionWrapper>
        {isDisabled && !submittedSuccessfully ? (
          <ConfirmDescriptionWrapper>
            <ErrorText>
              {formatMessage(
                type === RequestAction.Access
                  ? takeControlConfirmMessages.disabledWorkflowForAccess
                  : takeControlConfirmMessages.disabledWorkflow,
              )}
            </ErrorText>
          </ConfirmDescriptionWrapper>
        ) : (
          <DataSubjectRequestForm
            subjectType={subjectType}
            activeAction={action}
            coreIdentifier={coreIdentifier}
            activeDataSubjectId={activeDataSubjectId}
            onSubmit={confirmRequest}
            generalError={
              !error ? undefined : formatErrorMessage(error.message)
            }
            successMessage={
              submittedSuccessfully
                ? formatMessage(takeControlConfirmMessages.success)
                : undefined
            }
          />
        )}
      </TakeControlConfirmWrapper>
    </div>
  );
}

export default TakeControlConfirm;
